import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import {
  Form,
  SaveButton,
  TextInput,
  useNotify,
  useTranslate,
  required,
  useDataProvider,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { Resources } from '../resource';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import {agentCreateReferral} from "../../services/agentCreateReferral";
import {inviteCodeConfigUrl} from "../../services/inviteCodesConfig";
import {baseUrl, httpClient} from "../../providers/AppDataProvider";

function ConfigInitializer({
  setLoading,
  setCurrentUrl,
}: {
  setLoading: (v: boolean) => void;
  setCurrentUrl: (url: string) => void;
}) {
  const { setValue } = useFormContext();
  const didFetch = useRef(false);
  const notify = useNotify();

  useEffect(() => {
    if (didFetch.current) return;
    didFetch.current = true;

    const fetchData = async () => {
      try {
        const response = await inviteCodeConfigUrl({});
        console.log('=== inviteCodeConfigUrl response ===', response);
        
        // inviteCodeConfigUrl 返回的是 res.data?.data ?? res.data
        // 所以 response 可能是 { url: "..." } 或 { data: {...}, errors: [...] }
        if (response?.data === null && response?.errors?.length > 0) {
          notify(response.errors[0].message || 'module.agentInfo.FailToCreateReferral', {
            type: 'error',
          });
          setLoading(false);
          return;
        }
        
        // 直接访问 url，因为 inviteCodeConfigUrl 已经处理了数据结构
        const urlValue = response?.url ?? '';
        console.log('=== urlValue extracted ===', urlValue);
        
        if (urlValue) {
          setValue('url', urlValue);
          setCurrentUrl(urlValue);
        } else {
          setValue('url', '');
          setCurrentUrl('');
        }
      } catch (e: any) {
        console.error('=== fetchData error ===', e);
        // 如果获取失败，可能是配置不存在，使用空值
        setValue('url', '');
        setCurrentUrl('');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading, setValue, notify, setCurrentUrl]);

  return null;
}

function UrlInput({ currentUrl }: { currentUrl: string }) {
  const urlValue = useWatch({ name: 'url' });
  const hasValue = Boolean(currentUrl || urlValue);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <TextInput
        source="url"
        label={currentUrl || ''}
        placeholder="URL"
        validate={[required()]}
        fullWidth
        sx={
          hasValue
            ? {
                '& .MuiInputBase-input': {
                  color: 'text.secondary',
                },
              }
            : {}
        }
      />
    </Box>
  );
}

export function InviteBetaCodeConfigForm() {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string>('');



  const onSubmit = async (values: any) => {
    setSaving(true);
    try {


      const url = `${baseUrl}/invite-beta-code-config`;
      const payload ={ url: values.url }

        const { json } = await httpClient(url, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        console.log("===invite-beta-code-config === ",json);

      // 更新 currentUrl 以反映新的值
      setCurrentUrl(values.url);

      notify(
        translate('module.inviteBetaCodeConfig.updateSuccess') || 'Config updated successfully',
        { type: 'success' }
      );
    } catch (error: any) {
      const errorCode = error?.body?.code || error?.code;
      switch (errorCode) {
        case 'INTERNAL_SERVER_ERROR':
          notify(error?.message, { type: 'error' });
          break;
        default:
          notify(
            error?.message ||
              translate('module.inviteBetaCodeConfig.unknownErrorUpdate') ||
              'Unknown error occurred',
            { type: 'error' }
          );
          break;
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={onSubmit} defaultValues={{ url: '' }} disableInvalidFormNotification>
      <ConfigInitializer setLoading={setLoading} setCurrentUrl={setCurrentUrl} />
      {loading && <div>Loading...</div>}
      {!loading && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
          >
            <Typography variant="body2" sx={{ flexBasis: '20%', flexShrink: 0 }}>
              URL
            </Typography>
            <UrlInput currentUrl={currentUrl} />
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            mb={1}
          >
            <PermissionGuard resource="invite-beta-code-config" action="update" fallback={<></>}>
              <SaveButton disabled={saving} />
            </PermissionGuard>
          </Stack>
        </>
      )}
    </Form>
  );
}
